/**
 * batch-68 werkplaatsfacturen (FO-341) - PURE regels (geen Prisma, geen I/O,
 * geen `new Date()` buiten een meegegeven peildatum), gedeeld door API en UI.
 *
 *   1. Voertuig-match op kenteken + chassisnummer (de vijf regels uit §3.2).
 *   2. Plausibiliteit (gele hints, nooit een blokkade): merk en datum deel I.
 *   3. Sectieclassificatie: trefwoord van de garage -> AI -> afgeleid uit regels.
 *   4. Interval per voertuig: override -> merk+model -> merk -> soort-standaard.
 *   5. Onderhoudsbadge-status (op schema / binnenkort / achterstallig).
 *   6. Werkdagen (de vijf werkdagen van de APK-bevestiging) en de
 *      documentnaam van de werkplaatsfactuur.
 */

import {
  trefwoordSleutel,
  type IntervalBron,
  type OnderhoudBadgeStatus,
  type OnderhoudRegelClassificatie,
  type WerkplaatsMatchVia,
  type WerkplaatsSectieBron,
} from '@streep/types'
import { formatKenteken, normalizeKenteken, verwijderOnzichtbareTekens } from './voertuig-utils'
import { isNlWerkdagYmd } from './besteladvies'
import { ymdPlusDagen } from './gespreksverslag-deadline'

// ─── 1. Voertuig-match (FO-341 §3.2) ─────────────────────────────────────────

export interface MatchKandidaat {
  id: string
  /** Chassisnummer zoals op het voertuig (mag leeg zijn). */
  chassisNummer: string | null
}

export type VoertuigMatchUitkomst =
  | {
      soort: 'EENDUIDIG'
      voertuigId: string
      via: WerkplaatsMatchVia
      /** Leeg chassisnummer van het voertuig vullen met het VIN van de factuur. */
      chassisNummerVullen: boolean
      /** Bij een match via VIN: het kenteken op de factuur hoort niet bij dit voertuig. */
      kentekenAfwijking: boolean
    }
  | {
      soort: 'CONFLICT'
      /** Het voertuig van het kenteken. */
      voertuigId: string
      /** Het voertuig dat het VIN aanwijst (null = het VIN wijkt af maar is onbekend). */
      vinVoertuigId: string | null
      reden: string
    }
  | { soort: 'ONBEKEND'; reden: string }
  | { soort: 'MEERDERE'; aantal: number; reden: string }

/** Chassisnummer vergelijken zonder hoofdletter-/spatieverschil. */
function vinSleutel(v: string | null | undefined): string | null {
  if (!v) return null
  const s = verwijderOnzichtbareTekens(v).toUpperCase().replace(/[^A-Z0-9]/g, '')
  return s.length > 0 ? s : null
}

/**
 * De vijf regels (FO-341 §3.2), puur:
 *
 * | kenteken | chassisnummer                         | resultaat                              |
 * |----------|---------------------------------------|----------------------------------------|
 * | match    | match (zelfde voertuig)               | eenduidig                              |
 * | match    | ontbreekt op factuur of voertuig      | eenduidig; leeg chassisnummer vullen   |
 * | match    | ander voertuig of afwijkend           | werkvoorraad (conflict)                |
 * | geen     | match                                 | eenduidig via VIN (kentekenwissel)     |
 * | geen     | geen                                  | werkvoorraad (onbekend voertuig)       |
 *
 * `kentekenKandidaten` = voertuigen (actief én historisch) met dit genormaliseerde
 * kenteken; `vinKandidaat` = het voertuig met dit chassisnummer (of null).
 */
export function bepaalVoertuigMatch(input: {
  kentekenKandidaten: readonly MatchKandidaat[]
  /** Genormaliseerd VIN van de factuur, of null. */
  vin: string | null
  vinKandidaat: MatchKandidaat | null
}): VoertuigMatchUitkomst {
  const vin = vinSleutel(input.vin)
  if (input.kentekenKandidaten.length > 1) {
    return {
      soort: 'MEERDERE',
      aantal: input.kentekenKandidaten.length,
      reden: `Kenteken matcht ${input.kentekenKandidaten.length} voertuigen; kies handmatig.`,
    }
  }
  const viaKenteken = input.kentekenKandidaten[0] ?? null
  if (viaKenteken) {
    if (!vin) {
      return { soort: 'EENDUIDIG', voertuigId: viaKenteken.id, via: 'KENTEKEN', chassisNummerVullen: false, kentekenAfwijking: false }
    }
    const eigenVin = vinSleutel(viaKenteken.chassisNummer)
    if (eigenVin === vin) {
      return { soort: 'EENDUIDIG', voertuigId: viaKenteken.id, via: 'KENTEKEN_VIN', chassisNummerVullen: false, kentekenAfwijking: false }
    }
    if (input.vinKandidaat && input.vinKandidaat.id !== viaKenteken.id) {
      return {
        soort: 'CONFLICT',
        voertuigId: viaKenteken.id,
        vinVoertuigId: input.vinKandidaat.id,
        reden: 'Kenteken en chassisnummer spreken elkaar tegen: het chassisnummer hoort bij een ander voertuig.',
      }
    }
    if (!eigenVin) {
      return { soort: 'EENDUIDIG', voertuigId: viaKenteken.id, via: 'KENTEKEN', chassisNummerVullen: true, kentekenAfwijking: false }
    }
    return {
      soort: 'CONFLICT',
      voertuigId: viaKenteken.id,
      vinVoertuigId: null,
      reden: 'Kenteken en chassisnummer spreken elkaar tegen: het chassisnummer op de factuur wijkt af van dat van het voertuig.',
    }
  }
  if (vin && input.vinKandidaat) {
    return {
      soort: 'EENDUIDIG',
      voertuigId: input.vinKandidaat.id,
      via: 'VIN',
      chassisNummerVullen: false,
      kentekenAfwijking: true,
    }
  }
  return { soort: 'ONBEKEND', reden: 'Onbekend voertuig: geen STREEP-voertuig met dit kenteken of chassisnummer.' }
}

// ─── 2. Plausibiliteit (FO-341 §3.2) ─────────────────────────────────────────

function tokens(tekst: string | null | undefined): string[] {
  if (!tekst) return []
  return verwijderOnzichtbareTekens(tekst)
    .toUpperCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^A-Z0-9]+/)
    .filter((t) => t.length > 0)
}

function nlDag(iso: string): string {
  return `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}`
}

/**
 * Gele hints op de registratie (nooit een blokkade): een merk op de factuur dat
 * niet bij het voertuig past, of een "datum deel I" die afwijkt van de datum
 * eerste toelating. Datums als ISO-dag (jjjj-mm-dd).
 */
export function werkplaatsPlausibiliteit(input: {
  factuurMerkType: string | null
  voertuigMerk: string | null
  datumDeelI: string | null
  datumEersteToelating: string | null
}): string[] {
  const hints: string[] = []
  const merkTokens = tokens(input.voertuigMerk)
  const factuurTokens = tokens(input.factuurMerkType)
  if (merkTokens.length > 0 && factuurTokens.length > 0 && !factuurTokens.includes(merkTokens[0]!)) {
    hints.push(
      `Merk op de factuur (${input.factuurMerkType!.trim()}) past niet bij het voertuig (${input.voertuigMerk!.trim()}); controleer of de factuur bij dit voertuig hoort.`,
    )
  }
  if (input.datumDeelI && input.datumEersteToelating && input.datumDeelI !== input.datumEersteToelating) {
    hints.push(
      `Datum deel I op de factuur (${nlDag(input.datumDeelI)}) wijkt af van de datum eerste toelating van het voertuig (${nlDag(input.datumEersteToelating)}).`,
    )
  }
  return hints
}

// ─── 3. Sectieclassificatie (FO-341 §3.3) ────────────────────────────────────

export interface TrefwoordRegel {
  trefwoord: string
  /** `trefwoordSleutel(trefwoord)` (zoals opgeslagen). */
  sleutel: string
  classificatie: OnderhoudRegelClassificatie
}

export interface SectieTekst {
  titel: string
  arbeid: ReadonlyArray<{ code: string | null; omschrijving: string }>
}

/** Bij gelijke trefwoordlengte wint de classificatie die het zwaarst weegt. */
const CLASSIFICATIE_GEWICHT: readonly OnderhoudRegelClassificatie[] = [
  'BEURT_GROOT',
  'BEURT_KLEIN',
  'APK_KEURING',
  'SCHADE',
  'REPARATIE',
  'BANDEN',
  'KLEIN_ONDERHOUD',
  'OVERIG',
]

/**
 * Zoek het trefwoord van de garage dat deze sectie vastlegt. Vergelijkt als hele
 * woorden/frase op de sectietitel en op elke arbeidsregel ("B06 Onderhoud, Olie
 * verversing"). Het langste trefwoord wint (het meest specifiek: "B06 Onderhoud"
 * boven "B06"); bij gelijke lengte de zwaarste classificatie.
 */
export function zoekTrefwoordVoorSectie(
  sectie: SectieTekst,
  trefwoorden: readonly TrefwoordRegel[],
): TrefwoordRegel | null {
  if (trefwoorden.length === 0) return null
  const teksten = [
    sectie.titel,
    ...sectie.arbeid.map((a) => `${a.code ?? ''} ${a.omschrijving}`),
  ].map((t) => ` ${trefwoordSleutel(t)} `)
  let beste: TrefwoordRegel | null = null
  for (const t of trefwoorden) {
    const sleutel = t.sleutel.trim()
    if (sleutel.length === 0) continue
    if (!teksten.some((tekst) => tekst.includes(` ${sleutel} `))) continue
    if (
      beste === null ||
      sleutel.length > beste.sleutel.length ||
      (sleutel.length === beste.sleutel.length &&
        CLASSIFICATIE_GEWICHT.indexOf(t.classificatie) < CLASSIFICATIE_GEWICHT.indexOf(beste.classificatie))
    ) {
      beste = t
    }
  }
  return beste
}

export interface SectieClassificatie {
  classificatie: OnderhoudRegelClassificatie
  bron: WerkplaatsSectieBron
  /** 0..1; een trefwoord is 1. */
  zekerheid: number
  trefwoord: string | null
}

/** De zwaarste classificatie uit een lijst (OVERIG als de lijst leeg is). */
export function zwaarsteClassificatie(
  lijst: readonly OnderhoudRegelClassificatie[],
): OnderhoudRegelClassificatie {
  let beste: OnderhoudRegelClassificatie = 'OVERIG'
  for (const c of lijst) {
    if (CLASSIFICATIE_GEWICHT.indexOf(c) < CLASSIFICATIE_GEWICHT.indexOf(beste)) beste = c
  }
  return beste
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0
  return Math.min(1, Math.max(0, n))
}

/**
 * De classificatie van één sectie: (1) een trefwoord van de garage legt hem
 * deterministisch vast; (2) anders de AI-sectieclassificatie met haar zekerheid
 * (zonder eigen zekerheid: de zekerheid van de hele extractie); (3) anders de
 * zwaarste classificatie van de regels in de sectie, met `regelZekerheid`.
 */
export function bepaalSectieClassificatie(input: {
  sectie: SectieTekst & {
    aiClassificatie: OnderhoudRegelClassificatie | null
    aiZekerheid: number | null
  }
  trefwoorden: readonly TrefwoordRegel[]
  regelClassificaties: readonly OnderhoudRegelClassificatie[]
  /** Zekerheid van de hele extractie (0..1), of null als onbekend. */
  extractieZekerheid: number | null
  /** Zekerheid van de regelclassificaties (1 = deterministisch trefwoord). */
  regelZekerheid: number
}): SectieClassificatie {
  const treffer = zoekTrefwoordVoorSectie(input.sectie, input.trefwoorden)
  if (treffer) {
    return { classificatie: treffer.classificatie, bron: 'TREFWOORD', zekerheid: 1, trefwoord: treffer.trefwoord }
  }
  if (input.sectie.aiClassificatie) {
    return {
      classificatie: input.sectie.aiClassificatie,
      bron: 'AI',
      zekerheid: clamp01(input.sectie.aiZekerheid ?? input.extractieZekerheid ?? 0),
      trefwoord: null,
    }
  }
  return {
    classificatie: zwaarsteClassificatie(input.regelClassificaties),
    bron: 'REGELS',
    zekerheid: clamp01(input.regelZekerheid),
    trefwoord: null,
  }
}

/**
 * Classificaties die het type of een reset bepalen. Een twijfelende sectie met
 * alleen klein onderhoud of overig blokkeert de automatische registratie niet:
 * die reset niets en verandert het type niet.
 */
export function bepaaltTypeOfReset(c: OnderhoudRegelClassificatie): boolean {
  return c !== 'OVERIG' && c !== 'KLEIN_ONDERHOUD'
}

// ─── 4. Interval per voertuig (FO-341 §3.4) ──────────────────────────────────

export interface IntervalRijInput {
  soort: string
  merk: string | null
  model: string | null
  voertuigId: string | null
  maanden: number | null
  km: number | null
}

export interface VoertuigIntervalKern {
  id: string
  merk: string | null
  model: string | null
}

export interface IntervalResolutie {
  maanden: number | null
  km: number | null
  bron: Exclude<IntervalBron, 'STANDAARD'>
  rij: IntervalRijInput
}

/** Merk vergelijken: hoofdletters, leestekens als spatie ("Mercedes-Benz" = "MERCEDES BENZ"). */
export function merkSleutel(merk: string | null | undefined): string {
  return tokens(merk).join(' ')
}

/**
 * Past het model van de merk-rij bij het model van het voertuig? De woorden van
 * de rij moeten het begin van het voertuigmodel zijn: "CX-30" past bij
 * "CX-30 Skyactiv-G", maar "CX-3" niet bij "CX-30".
 */
export function modelPastBij(rijModel: string, voertuigModel: string | null | undefined): boolean {
  const rij = tokens(rijModel)
  const voertuig = tokens(voertuigModel)
  if (rij.length === 0 || rij.length > voertuig.length) return false
  return rij.every((t, i) => voertuig[i] === t)
}

/**
 * Het interval van één soort voor één voertuig, in de vaste volgorde: override
 * van het voertuig -> merk + model (het langste passende model) -> merk ->
 * null (= de soort-standaard, die de aanroeper invult).
 */
export function resolveerVoertuigInterval(
  soort: string,
  voertuig: VoertuigIntervalKern,
  rijen: readonly IntervalRijInput[],
): IntervalResolutie | null {
  const vanSoort = rijen.filter((r) => r.soort === soort)
  const override = vanSoort.find((r) => r.voertuigId === voertuig.id)
  if (override) return { maanden: override.maanden, km: override.km, bron: 'VOERTUIG', rij: override }
  const merk = merkSleutel(voertuig.merk)
  if (merk.length === 0) return null
  const merkRijen = vanSoort.filter((r) => r.voertuigId === null && r.merk !== null && merkSleutel(r.merk) === merk)
  const metModel = merkRijen
    .filter((r) => r.model !== null && modelPastBij(r.model, voertuig.model))
    .sort((a, b) => tokens(b.model).length - tokens(a.model).length)[0]
  if (metModel) return { maanden: metModel.maanden, km: metModel.km, bron: 'MERK_MODEL', rij: metModel }
  const alleenMerk = merkRijen.find((r) => r.model === null)
  if (alleenMerk) return { maanden: alleenMerk.maanden, km: alleenMerk.km, bron: 'MERK', rij: alleenMerk }
  return null
}

// ─── 5. Badge-status (FO-341 §3.4) ───────────────────────────────────────────

/**
 * Op schema / binnenkort / achterstallig. Achterstallig zodra de datum of de km
 * voorbij is; binnenkort binnen de drempel (dagen of km); onbekend zonder beide.
 */
export function onderhoudBadgeStatus(input: {
  dagenTot: number | null
  kmTot: number | null
  drempelDagen: number
  drempelKm: number
}): OnderhoudBadgeStatus {
  if (input.dagenTot === null && input.kmTot === null) return 'ONBEKEND'
  if ((input.dagenTot !== null && input.dagenTot < 0) || (input.kmTot !== null && input.kmTot < 0)) {
    return 'ACHTERSTALLIG'
  }
  if (
    (input.dagenTot !== null && input.dagenTot <= input.drempelDagen) ||
    (input.kmTot !== null && input.kmTot <= input.drempelKm)
  ) {
    return 'BINNENKORT'
  }
  return 'OK'
}

/** "APK uitgevoerd op 11-05-2026, nieuwe vervaldatum wordt bevestigd door de RDW." */
export function apkVoorlopigMelding(laatsteApkIso: string): string {
  return `APK uitgevoerd op ${nlDag(laatsteApkIso)}, nieuwe vervaldatum wordt bevestigd door de RDW.`
}

// ─── 6. Werkdagen en documentnaam ────────────────────────────────────────────

/**
 * `aantal` werkdagen (ma-vr, geen feestdag) na `startYmd`. De startdag telt niet
 * mee: vijf werkdagen na vrijdag 15-05 is vrijdag 22-05.
 */
export function plusWerkdagenYmd(
  startYmd: string,
  aantal: number,
  feestdagenYmd: ReadonlySet<string> = new Set(),
): string {
  let dag = startYmd
  let geteld = 0
  // Begrensd: hooguit een paar weken vooruit, nooit een eindeloze lus.
  for (let i = 0; i < 366 && geteld < aantal; i += 1) {
    dag = ymdPlusDagen(dag, 1)
    if (isNlWerkdagYmd(dag, feestdagenYmd)) geteld += 1
  }
  return dag
}

/**
 * De vergelijkingssleutel van een werkordernummer (FO-341 §3.5: één bezoek = één
 * registratie): zonder onzichtbare tekens, zonder voorvoegsel ("Werkorder",
 * "Werkordernr.", "WO", "W.O.", "nr", "#") en zonder spaties, in hoofdletters.
 * "WO 166556", "Werkorder: 166556" en "166 556" geven allemaal "166556". Een
 * code die met letters begint ("WORK-12", "A123") blijft heel. Null bij leeg.
 */
export function werkorderSleutel(v: string | null | undefined): string | null {
  if (!v) return null
  let s = verwijderOnzichtbareTekens(v).trim().toUpperCase()
  s = s.replace(/^(WERKORDER(NUMMER|NR\.?)?|W\.?\s?O\.?)(?=[\s.:#-]|\d)[\s.:#-]*/, '')
  s = s.replace(/^(NUMMER|NR\.?|NO\.?)(?=[\s.:#-]|\d)[\s.:#-]*/, '')
  s = s.replace(/^#\s*/, '').replace(/\s+/g, '')
  return s.length > 0 ? s : null
}

/**
 * Titel/bestandsnaam van de werkplaatsfactuur als voertuigdocument (FO-341 §3.5):
 * `Werkplaatsfactuur-S-807-RS-2026-05-11-50600093.pdf` (een creditnota heet
 * `Werkplaatscreditnota-...`). Het factuurnummer houdt alleen letters, cijfers,
 * `-` en `_` (een `/` of spatie wordt `-`).
 */
export function werkplaatsfactuurBestandsnaam(input: {
  kenteken: string
  werkDatumIso: string
  factuurNr: string
  creditnota?: boolean
}): string {
  const kenteken = formatKenteken(normalizeKenteken(input.kenteken)) || 'onbekend'
  const nr =
    verwijderOnzichtbareTekens(input.factuurNr)
      .trim()
      .replace(/[^A-Za-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'zonder-nummer'
  const soort = input.creditnota ? 'Werkplaatscreditnota' : 'Werkplaatsfactuur'
  return `${soort}-${kenteken}-${input.werkDatumIso}-${nr}.pdf`
}

// ─── 7. APK-bevestiging door de RDW (FO-341 §3.4) ────────────────────────────

/**
 * Een keuring levert (bij een APK-termijn van minstens een jaar, met hooguit
 * twee maanden vervroegen) een vervaldatum ruim na de keuringsdatum op. Een
 * RDW-datum die minstens zo ver na de werkdatum ligt, is dus al de NIEUWE datum
 * (de RDW was ons voor). Dit is uitsluitend een herkenningsgrens, nooit een
 * berekende vervaldatum: die komt altijd van de RDW.
 */
export const APK_RDW_HERKENNING_MIN_DAGEN = 300

export type ApkRdwOordeel = 'BEVESTIGD' | 'VOORLOPIG' | 'NIET_BIJ_RDW'

/**
 * Bevestigt de RDW de gefactureerde APK? BEVESTIGD zodra de RDW een vervaldatum
 * registreert die nieuwer is dan die van vóór de registratie (of al ruim na de
 * werkdatum ligt); anders VOORLOPIG tot `werkdagen` werkdagen na de start, en
 * daarna NIET_BIJ_RDW. Datums als ISO-dag; `rdwVervaldatum` null = de RDW kent
 * geen (of geen) datum. Was er vóór de registratie geen vervaldatum bekend, dan
 * bewijst alleen een datum ruim na de keuring dat de RDW de nieuwe keuring kent
 * (een oude datum vlak na de keuring is geen bevestiging).
 */
export function beoordeelApkRdwBevestiging(input: {
  rdwVervaldatum: string | null
  vervaldatumVoor: string | null
  werkDatum: string
  sindsYmd: string
  vandaagYmd: string
  werkdagen: number
  feestdagenYmd?: ReadonlySet<string>
}): ApkRdwOordeel {
  const rdw = input.rdwVervaldatum
  if (rdw) {
    const nieuwer = input.vervaldatumVoor !== null && rdw > input.vervaldatumVoor
    const ruimNaKeuring = rdw >= ymdPlusDagen(input.werkDatum, APK_RDW_HERKENNING_MIN_DAGEN)
    if ((nieuwer && rdw > input.werkDatum) || ruimNaKeuring) return 'BEVESTIGD'
  }
  const deadline = plusWerkdagenYmd(input.sindsYmd, input.werkdagen, input.feestdagenYmd)
  return input.vandaagYmd > deadline ? 'NIET_BIJ_RDW' : 'VOORLOPIG'
}

// ─── 7. Service-indicator na een beurt (FO-341 §3.4, verhouding tot FO-327) ──

/**
 * Na een geregistreerde beurt verwacht het systeem dat het volgende telematica-
 * rapport de teller gereset heeft. Staat het rapport 3 tot en met 10 dagen na de
 * werkdatum nog op achterstallig, dan vergat de garage de service-indicator. Een
 * jongere beurt krijgt nog de tijd (het rapport kan van vóór de reset zijn); een
 * oudere valt buiten de controle (dan is het gewoon weer een achterstallige
 * verwachting, het bestaande FO-327-signaal).
 */
export const SERVICE_INDICATOR_CONTROLE_MIN_DAGEN = 3
export const SERVICE_INDICATOR_CONTROLE_MAX_DAGEN = 10

/** Valt de beurt (werkdag) in het controlevenster van dit rapport (NL-dagen)? */
export function isServiceIndicatorControleVenster(beurtYmd: string, rapportYmd: string): boolean {
  const verschil = dagenTussenYmdStrikt(beurtYmd, rapportYmd)
  return verschil >= SERVICE_INDICATOR_CONTROLE_MIN_DAGEN && verschil <= SERVICE_INDICATOR_CONTROLE_MAX_DAGEN
}

function dagenTussenYmdStrikt(vanYmd: string, totYmd: string): number {
  const parse = (ymd: string): number => {
    const [y, m, d] = ymd.split('-').map((s) => parseInt(s, 10))
    return Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1)
  }
  return Math.round((parse(totYmd) - parse(vanYmd)) / 86_400_000)
}
